import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { faLocationDot } from '@fortawesome/pro-light-svg-icons/faLocationDot';
import { faPhone } from '@fortawesome/pro-light-svg-icons/faPhone';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Client from '@spike/client-model';
import { formatPhoneNumber } from '@spike/phone-utils';
import clsx from 'clsx';
import { useApiClientWrapper, useMarketplace, usePermissions } from 'hooks';
import { FunctionComponent, useEffect } from 'react';
import ExpandableSection from './ExpandableSection';
import InfoLine from './InfoLine';
import Tags from './Tags';
import MembershipIcon from './Memberships/MembershipIcon';
import { Button } from 'components/UI';
import { faCommentAltLines } from '@fortawesome/pro-regular-svg-icons';
import { fetchTwoWaySmsCountByCustomerThunk } from 'actions/twoWaySms/twoWaySmsActions';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { getClientProfileOverviewThunk } from '@spike/clients-action';
import { ClientProfileOverview } from '@spike/client-model';
import { useMemo } from 'react';
import { requestProfileOverviewOnce } from './requestProfileOverviewOnce';
import { requestSmsCountByCustomerOnce } from './requestSmsCountByCustomerOnce';
import { CardOnFile } from '@spike/payments-model';
import { getCardsOnFileByCustomersThunk } from '@spike/payments-action';
import { CardOnFileIndicator } from 'components/Bookings2/List/CardOnFile/CardOnFileIndicator';

export interface PetParentProps {
    client: Client;
    initExpanded?: boolean;
    disableExpandable?: boolean;
    matchMemebrships?: boolean;
    className?: string;
    onShowClient?: () => void;
    onClickChat?: () => void;
    validatePermissions?: boolean;
    cardOnFileMarketplaceId?: number;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        nameContainer: {
            display: 'flex',
            alignItems: 'center'
        },
        nameRow: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap'
        },
        membershipIcon: {
            marginLeft: 0
        },
        name: {
            fontSize: 16,
            fontWeight: 600,
            lineHeight: '100%'
        },
        owesPill: {
            color: '#CC444B',
            fontSize: 13,
            fontWeight: 500,
            borderRadius: 100,
            padding: '4px 10px',
            backgroundColor: '#FFE5E7',
            lineHeight: '20px',
            display: 'inline-flex',
            alignItems: 'center',
            position: 'relative',
            top: -2
        },
        infoLine: {
            marginTop: 9
        },
        tags: {
            marginTop: 13
        },
        clickeable: {
            'cursor': 'pointer',
            '&:hover': {
                textDecoration: 'underline'
            }
        },
        headerContainer: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
        },
        chatButtonContainer: {
            position: 'relative'
        },
        chatButton: {
            'borderColor': '#000',
            'borderRadius': 30,
            'height': 39,
            'minWidth': 'unset',
            'marginRight': '16px',
            'width': 39,
            'padding': 0,

            '& span': {
                paddingTop: '1px',
                lineHeight: 1,
                margin: '0px !important'
            },

            '& svg': {
                width: 18,
                height: 'auto'
            }
        },
        notificationBadge: {
            top: -5,
            left: -3,
            color: '#fff',
            fontSize: 13,
            lineHeight: '12.143px',
            display: 'flex',
            fontWeight: 600,
            borderRadius: 3,
            transform: 'none',
            alignItems: 'center',
            padding: '2px 5px 1px',
            justifyContent: 'center',
            backgroundColor: '#EF4F57',
            width: 20,
            height: 20,
            position: 'absolute'
        }
    })
);

export const PetParent: FunctionComponent<PetParentProps> = props => {
    const classes = useStyles(props);
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const marketplace = useMarketplace();
    const permissions = usePermissions();
    const canViewSensitiveContact = !props.validatePermissions || permissions.clients.viewSensitiveContact;
    const canViewChat = !props.validatePermissions || permissions.twoWaySms;

    const tags = [...props.client.tags.positive, ...props.client.tags.negative];

    const smsCountByCustomer = useSelector<RootState, number | null>(state => state.twoWaySms.customerMessageAmount);
    const profileOverview = useSelector<RootState, ClientProfileOverview | undefined>(
        state => state.clients.profileOverview
    );
    const cardsOnFile = useSelector<RootState, CardOnFile[]>(state => {
        const clientId = props.client.id;
        const marketplaceId = props.cardOnFileMarketplaceId;
        if (marketplaceId == null || clientId == null) return [];
        return state.payments.cardsOnFileByMarketplaceAndCustomer[marketplaceId]?.[clientId] ?? [];
    });
    const isLoadingCardsOnFile = useSelector<RootState, boolean>(state => {
        if (props.cardOnFileMarketplaceId == null) return false;
        return state.payments.cardsOnFileLoadingByMarketplace[props.cardOnFileMarketplaceId] ?? false;
    });
    const hasCardsOnFileResult = useSelector<RootState, boolean>(state => {
        const clientId = props.client.id;
        if (props.cardOnFileMarketplaceId == null || clientId == null) return false;
        const customers = state.payments.cardsOnFileByMarketplaceAndCustomer[props.cardOnFileMarketplaceId];
        return customers != null && Object.prototype.hasOwnProperty.call(customers, clientId);
    });

    const parseMoney = (value: unknown): number => {
        if (typeof value === 'number') return value;
        if (typeof value !== 'string') return Number.NaN;
        return parseFloat(value.replace(/[^0-9.-]+/g, ''));
    };

    const getProfileCustomerId = (overview: ClientProfileOverview | undefined): number | undefined => {
        const anyOverview = overview as any;
        return (
            anyOverview?.customer?.id ??
            anyOverview?.customerId ??
            anyOverview?.customer?.customerId ??
            anyOverview?.customer?.clientId
        );
    };

    const clientOwes = useMemo(() => {
        if (!props.client?.id || !profileOverview) return false;

        const overviewCustomerId = getProfileCustomerId(profileOverview);
        if (overviewCustomerId != null && overviewCustomerId !== props.client.id) return false;

        const debt = (profileOverview as any)?.outstandingInvoices?.invoiceDebt;
        const amount = parseMoney(debt);
        return !Number.isNaN(amount) && amount > 0;
    }, [props.client?.id, profileOverview]);

    useEffect(() => {
        if (!props.client?.id) return;
        requestSmsCountByCustomerOnce(props.client.id, props.cardOnFileMarketplaceId, () => {
            dispatch(fetchTwoWaySmsCountByCustomerThunk(props.client?.id!, props.cardOnFileMarketplaceId));
        });
    }, [props.client?.id, props.cardOnFileMarketplaceId]);

    useEffect(() => {
        if (!props.client?.id) return;
        requestProfileOverviewOnce(props.client.id, () => {
            dispatch(getClientProfileOverviewThunk(apiClientWrapper, props.client?.id!));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.client?.id]);

    useEffect(() => {
        if (!props.client?.id || props.cardOnFileMarketplaceId == null || hasCardsOnFileResult || isLoadingCardsOnFile)
            return;
        dispatch(getCardsOnFileByCustomersThunk(apiClientWrapper, props.cardOnFileMarketplaceId, [props.client.id]));
    }, [hasCardsOnFileResult, isLoadingCardsOnFile, props.cardOnFileMarketplaceId, props.client?.id]);

    const header = (
        <Box className={classes.headerContainer}>
            <Box>
                <Box className={classes.nameContainer}>
                    <Box className={classes.nameRow}>
                        <Typography
                            className={clsx(classes.name, { [classes.clickeable]: props.onShowClient })}
                            onClick={props.onShowClient}
                        >{`${props.client.firstName} ${props.client.lastName}`}</Typography>
                        {props.matchMemebrships && <MembershipIcon className={classes.membershipIcon} />}
                        {clientOwes && <span className={classes.owesPill}>Owes</span>}
                        {props.cardOnFileMarketplaceId != null && (
                            <CardOnFileIndicator
                                cards={cardsOnFile}
                                isLoading={isLoadingCardsOnFile}
                                showBelow
                            />
                        )}
                    </Box>
                </Box>
                {canViewSensitiveContact && (
                    <InfoLine
                        icon={faPhone}
                        className={classes.infoLine}
                    >
                        {formatPhoneNumber(props.client.phone, marketplace.basics.address.country!.id)}
                    </InfoLine>
                )}
            </Box>
            {props.onClickChat && canViewChat && (
                <div className={classes.chatButtonContainer}>
                    <Button
                        startIcon={faCommentAltLines}
                        color="black"
                        variant="secondary"
                        onClick={props.onClickChat}
                        className={classes.chatButton}
                        size="medium"
                    />
                    {(smsCountByCustomer ?? 0) > 0 && (
                        <span className={classes.notificationBadge}>{smsCountByCustomer}</span>
                    )}
                </div>
            )}
        </Box>
    );

    return (
        <ExpandableSection
            header={header}
            initExpanded={props.initExpanded ?? true}
            className={props.className}
        >
            {canViewSensitiveContact && !props.client.temporalEmail && (
                <InfoLine
                    icon={faEnvelope}
                    className={classes.infoLine}
                >
                    {props.client.email}
                </InfoLine>
            )}
            {canViewSensitiveContact && (
                <InfoLine
                    icon={faLocationDot}
                    className={classes.infoLine}
                >
                    {props.client.address !== undefined
                        ? `${props.client.address?.street ?? ''}${
                              props.client.address?.suite ?? '' !== '' ? ' ' + props.client.address?.suite : ''
                          }, ${props.client.address?.city ?? ''}, ${props.client.address?.state ?? ''}`
                        : '—'}
                </InfoLine>
            )}
            {canViewSensitiveContact && (tags.length > 0 || props.client.blocked) && (
                <Tags
                    blockedClient={props.client.blocked}
                    options={tags}
                    className={classes.tags}
                />
            )}
        </ExpandableSection>
    );
};

export default PetParent;
